import type { Ansprechpartner } from "@repo/shared";

/** Bearbeitbare Zeile – Texte statt null, dazu eine feste Kennung für React. */
export type AnsprechpartnerEntwurf = {
  kennung: string;
  vorname: string;
  nachname: string;
  funktion: string;
  email: string;
  telefon: string;
  mobil: string;
};

const FELDER = [
  ["vorname", "Vorname"],
  ["nachname", "Nachname *"],
  ["funktion", "Funktion"],
  ["email", "E-Mail"],
  ["telefon", "Telefon"],
  ["mobil", "Mobil"],
] as const;

/** Längen wie im API-Schema – so scheitern zu lange Werte nicht erst beim Speichern. */
const MAX_LAENGE: Record<(typeof FELDER)[number][0], number> = {
  vorname: 120,
  nachname: 120,
  funktion: 120,
  email: 255,
  telefon: 64,
  mobil: 64,
};

export function neuerEntwurf(): AnsprechpartnerEntwurf {
  return { kennung: crypto.randomUUID(), vorname: "", nachname: "", funktion: "", email: "", telefon: "", mobil: "" };
}

export function alsEntwurf(p: Ansprechpartner): AnsprechpartnerEntwurf {
  return {
    kennung: crypto.randomUUID(),
    vorname: p.vorname ?? "",
    nachname: p.nachname,
    funktion: p.funktion ?? "",
    email: p.email ?? "",
    telefon: p.telefon ?? "",
    mobil: p.mobil ?? "",
  };
}

/** Eine ganz leere Zeile wird beim Speichern weggelassen. */
export function istLeer(e: AnsprechpartnerEntwurf): boolean {
  return FELDER.every(([feld]) => e[feld].trim() === "");
}

export function ausEntwurf(e: AnsprechpartnerEntwurf): Ansprechpartner {
  const text = (w: string) => (w.trim() === "" ? null : w.trim());
  return {
    vorname: text(e.vorname),
    nachname: e.nachname.trim(),
    funktion: text(e.funktion),
    email: text(e.email),
    telefon: text(e.telefon),
    mobil: text(e.mobil),
  };
}

/**
 * Ansprechpartner pflegen: hinzufügen, ändern, entfernen und verschieben.
 * Der erste in der Liste ist die Kontaktperson.
 */
export function AnsprechpartnerEditor({
  liste,
  fehler,
  onChange,
}: {
  liste: AnsprechpartnerEntwurf[];
  /** Fehlertext je `kennung`. */
  fehler: Record<string, string>;
  onChange: (liste: AnsprechpartnerEntwurf[]) => void;
}) {
  function setze(i: number, feld: (typeof FELDER)[number][0], wert: string) {
    onChange(liste.map((p, j) => (j === i ? { ...p, [feld]: wert } : p)));
  }

  function verschiebe(i: number, richtung: -1 | 1) {
    const neu = [...liste];
    [neu[i], neu[i + richtung]] = [neu[i + richtung]!, neu[i]!];
    onChange(neu);
  }

  return (
    <fieldset className="ansprechpartner-liste">
      <legend>Ansprechpartner</legend>
      {liste.length === 0 && (
        <p className="muted small">Noch keine Ansprechpartner. Der erste in der Liste erscheint als Kontaktperson.</p>
      )}
      {liste.map((p, i) => (
        <div key={p.kennung} className="ansprechpartner">
          <div className="ansprechpartner__felder">
            {FELDER.map(([feld, label]) => (
              <label key={feld} className="field">
                <span>{label}</span>
                <input
                  type={feld === "email" ? "email" : "text"}
                  maxLength={MAX_LAENGE[feld]}
                  value={p[feld]}
                  onChange={(e) => setze(i, feld, e.target.value)}
                />
              </label>
            ))}
          </div>
          {fehler[p.kennung] && <small className="error">{fehler[p.kennung]}</small>}
          <div className="actions">
            <button type="button" disabled={i === 0} onClick={() => verschiebe(i, -1)}>
              Nach oben
            </button>
            <button type="button" disabled={i === liste.length - 1} onClick={() => verschiebe(i, 1)}>
              Nach unten
            </button>
            <button type="button" onClick={() => onChange(liste.filter((_, j) => j !== i))}>
              Entfernen
            </button>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...liste, neuerEntwurf()])}>
        Ansprechpartner hinzufügen
      </button>
    </fieldset>
  );
}
